import * as React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { CmdLoader } from '@commander-services/gui-components';
import { useIntl } from 'react-intl';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { BluecoinListApiResponse } from '../interfaces';
import TabComponent from '../../TabComponent';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import BluecoinsDataSchema from '../BluecoinsDataSchema.json';
import BluecoinsSettingsSchema from '../BluecoinsSettingsSchema.json';
import { CmdTable } from '../../CmdTable/CmdTable';
import {
  BLE_TABLE_NAME,
  BLE_TABLE_SETTINGS_KEY,
  BlucoinsTableTranslationKeys,
  CHECKBOX_KEY,
  IS_ACTIVE,
  IS_INACTIVE,
  LAST_USAGE_ADDRESS_KEY,
  STATE_KEY,
  USAGE_START_ADDRESS_KEY,
  VEHICLE_KEY,
} from '../constants';
import { ACTIONS_KEY, CUSTOMERS_KEY } from '../../CmdTable/constants';
import CmdTableSettings from '../../CmdTable/settings/CmdTableSettings';
import useBleList from './useBleList';
import { ICmdTableColumn, ICmdTableFilter } from '../../CmdTable/types';
import FilterChips from '../../CmdTable/FilterChips/FilterChips';
import { BluecoinRecord } from './BluecoinsListState';
import { customersAtom, ICustomer, selectedCustomersAtom } from '../../../store/recoil/customers';
import BulkOperations from '../../CmdTable/BulkOperations';
import { getTableName, getTableSettings, handleOrderColumns } from '../../CmdTable/CmdTableService';
import { activate, deactivate } from '../BluecoinsService';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
  tableFiltersAtomFamily,
} from '../../CmdTable/CmdTableState';
import useAnalytics from '../../../hooks/useAnalytics';

function Bluecoins(): JSX.Element {
  const { formatMessage: f } = useIntl();
  dayjs.extend(weekOfYear);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(BLE_TABLE_NAME));
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(BLE_TABLE_NAME)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(BLE_TABLE_NAME)
  );
  const { columns, columnOrder, columnVisibility } = useBleList();
  const [selectedRows, setSelectedRows] = React.useState<BluecoinRecord[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const { trackEvent } = useAnalytics();

  const documentTitle = `${f({ id: 'table_list.tabs.bluecoin.list' })} - ${f({
    id: 'menu.bluecoins',
  })}`;
  useDocumentTitle(documentTitle);

  const updateTable = () => {
    // setCallTable(true);
    queryClient.invalidateQueries({
      queryKey: [BLE_TABLE_NAME, filterForRequest, sortFilter, selectedCustomers],
    });
  };

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[],
    customerForSettings: ICustomer[]
  ) => {
    const orderColumns = handleOrderColumns(newOrderColumns, customerForSettings);

    setColumnsTable(orderColumns);
  };

  const getSettings = async () => {
    const settings = await getTableSettings(BLE_TABLE_NAME, BluecoinsSettingsSchema);
    if (settings) {
      handleSetOrderColumns(settings.columns, customer);

      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['bleCmdTableSettings'],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingSettings } = results[0];

  // const handleAddButton = (e: React.MouseEvent<HTMLAnchorElement>) => {
  //   e.preventDefault();
  //   navigate(`${URL_CODEBOOKS}/${TABLE_CODEBOOKS_RIDE}/add`);
  // };

  const isLoading = isLoadingSettings;

  const updateCellValues = (ids: number[], key: string, value: string) => {
    queryClient.setQueryData(
      [BLE_TABLE_NAME, filterForRequest, sortFilter, selectedCustomers],
      (oldData: any) => {
        if (!oldData) return oldData;
        const newData = {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            records: page.records.map((record: BluecoinRecord) =>
              ids.includes(record.id) ? { ...record, [key]: value } : record
            ),
          })),
        };
        return newData;
      }
    );
  };

  const handleActivate = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent([`table ${getTableName(BLE_TABLE_NAME)}`, 'click', 'Bulk Activate']);
    const bluecoinIds = selectedRows.map((row) => row.id);

    if (bluecoinIds.length > 0) {
      const response = await activate(bluecoinIds);
      if (response) {
        updateCellValues(bluecoinIds, STATE_KEY, IS_ACTIVE);
      }
    }
  };
  const handleDeactivate = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent([`table ${getTableName(BLE_TABLE_NAME)}`, 'click', 'Bulk Deactivate']);
    const bluecoinIds = selectedRows.map((row) => row.id);

    if (bluecoinIds.length > 0) {
      const response = await deactivate(bluecoinIds);
      if (response) {
        updateCellValues(bluecoinIds, STATE_KEY, IS_INACTIVE);
      }
    }
  };

  const handleRowChange = (data: BluecoinRecord[], tableCount: number) => {
    setSelectedRows(data);
    setTotalCount(tableCount);
  };

  return (
    <div>
      <div className="w-page">
        <div className="w-page-content">
          <div className="page-header">
            <h4 className="page-header__title">{f({ id: 'menu.bluecoins' })}</h4>
            {/* <OnlineHelp topic="bluecoins" /> */}
          </div>
          <div className="container-fluid">
            <TabComponent
              headerStyles={{ marginTop: 0 }}
              tabs={[
                {
                  name: f({ id: 'table_list.tabs.bluecoin.list' }),
                  url: '/bluecoins',
                  isLink: true,
                  dataCy: 'bluecoins-list',
                  isActive: true,
                },
                {
                  name: f({ id: 'table_list.tabs.bluecoin.usage' }),
                  url: '/bluecoins/usage',
                  isLink: true,
                  dataCy: 'bluecoins-usage',
                },
              ]}
            />

            <section className="basic-box border-top-thin border-top-gray">
              <div
                className="basic-box__content"
                // style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}
              >
                <div className="row no-gutters mb-2">
                  <div className="col-12 col-md-auto mr-md-2 mb-2" />
                  <div className="col col-md" />
                  <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" />
                  <div className="col-auto order-4 order-lg-5">
                    <CmdTableSettings
                      tableId={BLE_TABLE_NAME}
                      // actionColumnEnabled={true}
                      tableName={BLE_TABLE_SETTINGS_KEY}
                      sort={{ column: CUSTOMERS_KEY, direction: 'desc' }}
                      // translationKeys={TableSettingsKeys}
                      translationKeys={BlucoinsTableTranslationKeys}
                    />
                  </div>
                </div>
                <div className="d-flex flex-wrap mb-2">
                  {tableFiltersKey &&
                    tableFiltersKey.map((filterId) => (
                      <FilterChips key={filterId} id={filterId} tableName={BLE_TABLE_NAME} />
                    ))}
                </div>
                {selectedRows.length > 0 && (
                  <BulkOperations<BluecoinRecord>
                    selectedRows={selectedRows}
                    actions={[
                      {
                        id: 'activate',
                        name: f({ id: 'table_list.actions.activate' }),
                        icon: IS_ACTIVE,
                        callback: handleActivate,
                      },
                      {
                        id: 'deactivate',
                        name: f({ id: 'table_list.actions.deactivate' }),
                        icon: IS_INACTIVE,
                        callback: handleDeactivate,
                      },
                    ]}
                    totalCount={totalCount}
                  />
                )}
                {isLoading && <CmdLoader inContent={true} />}
                <CmdTable<BluecoinListApiResponse, BluecoinRecord>
                  name={BLE_TABLE_NAME}
                  // fetchSize={20}
                  stickyColumns={[ACTIONS_KEY]}
                  lockedColumns={[
                    CHECKBOX_KEY,
                    USAGE_START_ADDRESS_KEY,
                    LAST_USAGE_ADDRESS_KEY,
                    VEHICLE_KEY,
                  ]}
                  columns={columns}
                  columnOrder={columnOrder}
                  columnVisibility={columnVisibility}
                  onSelectedRowsChange={handleRowChange}
                  jsonSchema={BluecoinsDataSchema}
                  leftStickyColumn={[CHECKBOX_KEY]}
                />
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(Bluecoins);
